import { getCardBrandDisplay } from "@Light/components/account/PaymentMethod/PaymentMethodCard/PaymentMethodCard";
import { formatServiceEvent } from "@Light/components/enroll/serviceEvent";
import { Skeleton } from "@Light/components/page/Skeleton";
import {
  DocumentLink,
  getPlanHeaderImageForFeatures,
} from "@Light/components/rateOffer";
import { useScaffold } from "@Light/scaffold";
import { ServiceEvent, type PlanLink } from "@Light/services/lightTypes";
import { formatAddress } from "@Light/utils/address";
import {
  formatDisplayDate,
  formatMonthDayYear,
  parseLocalDate,
} from "@Light/utils/dateHelpers";
import { formatBrandedPlanName } from "@Light/utils/formatting";
import { formatPhoneNumberDashed } from "@Light/utils/phoneNumber";
import { usePlan } from "@Light/utils/plan";
import { getPlanLinks } from "@Light/utils/planLinks";
import { PlanFeatures } from "@Light/utils/planFeatures";
import {
  BankNote01,
  BatteryFull,
  Car02,
  HeartHand,
  Star01,
  Sun,
} from "@untitledui/icons";
import { Fragment } from "react";
import { useEnrollment } from "../flow/context";
import { maybeRequirementValue, requirementValue } from "../flow/screen";
import { EnrollRoute } from "../routes";
import {
  EnrollmentConfirmationCard,
  EnrollmentConfirmationCardFooter,
  EnrollmentRequirement,
} from "./EnrollmentConfirmationCard";
import { usePatchedRate } from "./usePatchedRate";

const listSeparator = (index: number, count: number): string | null => {
  if (index === 0) return null;
  if (count === 2) return " and ";
  return index === count - 1 ? ", and " : ", ";
};

const PlanLinks = ({ links }: { links: PlanLink[] }) => (
  <>
    {links.map(({ short_name, url }, index) => (
      <Fragment key={short_name}>
        {listSeparator(index, links.length)}
        <DocumentLink document={short_name} href={url}>
          <span className="underline">{short_name}</span>
        </DocumentLink>
      </Fragment>
    ))}
  </>
);

const hasFreeOvernightCharging = (features: PlanFeatures): boolean => {
  const vehicleCharging = features.vehicleCharging;
  if (!vehicleCharging) return false;

  // A monthly program fee means the charging isn't actually free.
  if (vehicleCharging.baseFee !== 0) return false;

  // Schedule includes midnight
  const overnightSchedule = vehicleCharging.schedules.some(
    (schedule) => schedule.end_time < schedule.start_time,
  );

  // Every tier is uncapped (unlimited kWh) at a $0 rate
  const chargingIsFree =
    vehicleCharging.onScheduleRates.length > 0 &&
    vehicleCharging.onScheduleRates.every(
      (tier) => !tier.cap && parseFloat(tier.rate) === 0,
    );

  return overnightSchedule && chargingIsFree;
};

const getPlanHighlight = (
  features: PlanFeatures,
): EnrollmentConfirmationCardFooter => {
  if (hasFreeOvernightCharging(features)) {
    return {
      icon: Car02,
      text: "Includes unlimited EV charging every night.",
    };
  }

  if (features.solarBuyback && features.storageCredit) {
    return {
      icon: BatteryFull,
      text: "Earns credit from your solar and battery.",
    };
  }

  if (features.solarBuyback) {
    return {
      icon: Sun,
      text: "Earns credits for your excess solar.",
    };
  }

  return {
    icon: Star01,
    text: `Includes highly-rated customer service.`,
  };
};

export const EnrollmentConfirmationCards = () => {
  const scaffold = useScaffold();
  const { features } = usePlan();
  const { requirements, navigate } = useEnrollment();

  const serviceAddress = requirementValue(requirements.service_address);
  const name = requirementValue(requirements.name);
  const email = requirementValue(requirements.email);
  const serviceEvent = requirementValue(requirements.service_event);
  const serviceStartDate = requirementValue(requirements.service_start_date);
  const phoneNumber = requirementValue(requirements.phone_number);
  const dob = requirementValue(requirements.dob);
  const paymentMethod = maybeRequirementValue(requirements.payment_method);
  const { data: rate } = usePatchedRate();

  return (
    <div className="flex flex-col gap-3xl">
      <EnrollmentConfirmationCard
        title={rate ? formatBrandedPlanName(scaffold.page, rate.name) : "Plan"}
        headerSrc={getPlanHeaderImageForFeatures(features)}
        editTestId="edit-plan-button"
        onEdit={() => {
          navigate(EnrollRoute.ConfirmRate);
        }}
        footer={getPlanHighlight(features)}
      >
        <EnrollmentRequirement label="Terms">
          {rate ? (
            <div className="text-balance">
              Low rate fixed for {rate.term_months} months
            </div>
          ) : (
            <Skeleton className="h-4 w-30" />
          )}
        </EnrollmentRequirement>
        <EnrollmentRequirement label="Address">
          <div className="line-clamp-2">{formatAddress(serviceAddress)}</div>
        </EnrollmentRequirement>
        <EnrollmentRequirement label="Plan documents">
          {rate ? (
            <PlanLinks links={getPlanLinks(rate)} />
          ) : (
            <Skeleton className="h-4 w-30" />
          )}
        </EnrollmentRequirement>
      </EnrollmentConfirmationCard>
      <EnrollmentConfirmationCard
        title="Details"
        editTestId="edit-identity-button"
        onEdit={() => {
          // TODO: Change `navigate` and `next` such that forward nav steps through visited screens again
          navigate(EnrollRoute.VerifyIdentity);
        }}
        footer={{
          text: "We'll set up service with your local utility.",
          icon: HeartHand,
        }}
      >
        <EnrollmentRequirement label="Name">
          <div className="truncate">{`${name.first} ${name.last}`}</div>
        </EnrollmentRequirement>
        <EnrollmentRequirement label="Email">
          <div className="truncate">{email.value}</div>
        </EnrollmentRequirement>
        <EnrollmentRequirement label="Phone number">
          {formatPhoneNumberDashed(phoneNumber.value)}
        </EnrollmentRequirement>
        <EnrollmentRequirement label="Birthday">
          {formatMonthDayYear(dob)}
        </EnrollmentRequirement>
        <EnrollmentRequirement label="Service type">
          {formatServiceEvent(serviceEvent.value as ServiceEvent)}
        </EnrollmentRequirement>
        <EnrollmentRequirement label="Service start date">
          {formatDisplayDate(parseLocalDate(serviceStartDate.value))}
        </EnrollmentRequirement>
      </EnrollmentConfirmationCard>
      {paymentMethod && (
        <EnrollmentConfirmationCard
          title="Payment method"
          tight
          onEdit={() => {
            navigate(EnrollRoute.PaymentSetup);
          }}
          footer={{
            text: "You won't be charged until your first bill.",
            icon: BankNote01,
          }}
        >
          <div className="text-sm text-secondary">
            {`${getCardBrandDisplay(paymentMethod.card?.brand)} ${paymentMethod.card?.last_four}`}
          </div>
        </EnrollmentConfirmationCard>
      )}
    </div>
  );
};
